import { Badge, ButtonBase, Stack, Typography, useTheme } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ChatOutlined, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useChatFilters from './useChatFilters';
import { ChatFilter } from '../../types/chats';
import DropdownMenu from '../global/DropdownMenu';
import { important } from '../../utils/global/helper';
import useSearchModal from '../../hooks/global/useSearchModal';
import { AppointmentHit } from '../../types/appointments';
import { createAppointmentChat } from '../../services/appointments';
import useUser from '../../hooks/global/useUser';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import { resetConversations } from '../../services/chats';
import { navigateToChat } from '../../helpers/appointments';

const getFilterLabel = (filter: ChatFilter) => {
  switch (filter) {
    case ChatFilter.ALL:
      return 'All';
    case ChatFilter.UNREADS:
      return 'Unread conversations';
    case ChatFilter.UNANSWERED:
      return 'Unanswered conversations';
    case ChatFilter.ARCHIVED:
      return 'Archived conversations';
    case ChatFilter.IMPORTANT:
      return 'Important conversations';
    default:
      return '';
  }
};

function InboxFilters() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useUser();
  const { open: openSearchModal } = useSearchModal();
  const { filter, onFilter } = useChatFilters();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const onSelectFilter = async (newFilter: ChatFilter) => {
    await resetConversations(newFilter);
    onFilter(newFilter);
  };

  return (
    <Stack
      direction="row"
      mb={1}
      gap={1}
      boxSizing="border-box"
      alignItems="stretch">
      <ButtonBase
        onClick={() =>
          openSearchModal({
            type: 'chat',
            appointmentStatus: null,
            appointmentsOnly: true,
            placeholder: 'Search customers',
            sort: {
              by: 'lastChatMessageAt',
              direction: 'desc',
            },
            itemButton: {
              content: <ChatOutlined htmlColor={theme.palette.darkGrey.main} />,
              onClick: async hit => {
                try {
                  if (hit.type !== 'appointment' || !hit.item.objectID || !user)
                    return;
                  openLoadingModal({
                    header: 'Loading chat',
                    message: 'Please wait while we load the chat',
                  });
                  const appointment = hit.item as AppointmentHit;
                  if (!appointment.status) return;
                  const chatResponse = await createAppointmentChat(
                    {
                      appointmentId: appointment.objectID,
                    },
                    user,
                  );
                  if (!chatResponse) return;
                  navigateToChat(navigate, chatResponse);
                } catch {
                  // Empty
                } finally {
                  closeLoadingModal();
                }
              },
            },
          })
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
          gap: 1,
          borderRadius: '40px',
          border: `1px solid ${theme.palette.lightGrey.main}`,
          px: 1.5,
          height: '50px',
          flex: 1,
        }}>
        <Search htmlColor={theme.palette.medGrey.main} />
        <Typography variant="body1" color="text.secondary">
          Search
        </Typography>
      </ButtonBase>

      <DropdownMenu
        button={{
          sx: {
            minWidth: important('40px'),
          },
          content: (
            <Badge
              color="primary"
              variant="dot"
              invisible={!filter || filter === ChatFilter.ALL}>
              <FilterListIcon
                htmlColor="black"
                sx={{
                  fontSize: '24px',
                }}
              />
            </Badge>
          ),
        }}
        options={[
          ChatFilter.ALL,
          ChatFilter.UNREADS,
          ChatFilter.IMPORTANT,
          ChatFilter.ARCHIVED,
        ].map(f => ({
          id: f,
          label: getFilterLabel(f),
          action: () => onSelectFilter(f),
        }))}
        selectedOption={filter}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      />
    </Stack>
  );
}

export default InboxFilters;
